import { useState, useCallback, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Footer from '../Components/Footer';
import Header from '../Components/Header';
import { loginUser, isTokenExpired } from '../utils/authUtils';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const Base_Url = process.env.REACT_APP_API_BASE_URL;
const Brands = process.env.REACT_APP_API_BRANDS;
const limit = parseInt(process.env.REACT_APP_API_LIMIT);

const Home = () => {
    const [brands, setBrands] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [confirmedSearch, setConfirmedSearch] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);




    const fetchSuggestions = useCallback(async (query) => {
        if (!query.trim()) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }
        try {
            const response = await axios.get(`${Base_Url}/shopping/products/suggestions?q=${encodeURIComponent(query)}`);
            const brandResults = response.data?.result?.brands || [];

            if (brandResults.length > 0) {
                const names = brandResults.map((b) => b.name);
                setSuggestions(names);
                setShowSuggestions(true);
            } else {
                setSuggestions([]);
                setShowSuggestions(false);
            }
        } catch (error) {
            console.error('Suggestion fetch failed:', error);
            setSuggestions([]);
            setShowSuggestions(false);
        }
    }, []);

    const fetchBrandsWithRetry = useCallback(async (token) => {
        setLoading(true);
        setError(null);

        try {
            const headers = { Authorization: `Bearer ${token}` };
            const response = await axios.get(`${Base_Url}/${Brands}/list?page=${currentPage}&limit=${limit}&search=${encodeURIComponent(confirmedSearch)}`, { headers });

            if (response.data.status === 'success') {
                setBrands(response.data.result.brands);
                setTotalPages(response.data.result.pagination.pages || 1);
            } else {
                throw new Error("Fetch failed");
            }
        } catch (error) {
            if (error.response?.status === 401) {
                try {
                    await loginUser();
                    const retryToken = localStorage.getItem('jwt_token');
                    return fetchBrandsWithRetry(retryToken);
                } catch (loginErr) {
                    console.error('Login retry failed:', loginErr);
                    setError('Session expired. Please reload.');
                }
            } else {
                console.error('Fetch error:', error);
                setError('Failed to load brands. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    }, [currentPage, confirmedSearch]);

    const init = useCallback(async () => {
        try {
            let token = localStorage.getItem('jwt_token');

            if (!token) {
                console.log('token not used');
                try {
                    await loginUser();
                    token = localStorage.getItem('jwt_token');
                } catch (loginErr) {
                    console.warn('Login failed. Trying register...');
                    // await registerUser();
                    // token = localStorage.getItem('jwt_token');
                }
            }

            if (token && isTokenExpired(token)) {
                await loginUser();
                token = localStorage.getItem('jwt_token');
            }

            await fetchBrandsWithRetry(token);
        } catch (err) {
            console.error("Initialization failed:", err);
            setError("Authentication failed. Please reload.");
        }
    }, [fetchBrandsWithRetry]);


    // Load as early as possible
    useLayoutEffect(() => {
        init();
    }, [currentPage, fetchBrandsWithRetry, confirmedSearch, init]);


    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            const brandsSection = document.getElementById('brands');
            if (brandsSection) {
                brandsSection.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };


    const handleRetry = () => {
        setError(null);
        setCurrentPage(1);
    };

    const renderPaginationItems = () => {
        const range = [];
        const delta = 2;
        const start = Math.max(2, currentPage - delta);
        const end = Math.min(totalPages - 1, currentPage + delta);

        range.push(1);

        if (start > 2) {
            range.push('...');
        }

        for (let i = start; i <= end; i++) {
            range.push(i);
        }

        if (end < totalPages - 1) {
            range.push('...');
        }

        if (totalPages > 1) {
            range.push(totalPages);
        }

        return range.map((page, i) =>
            page === '...' ? (
                <li key={`ellipsis-${i}`}>
                    <span className="page-numbers">...</span>
                </li>
            ) : (
                <li key={page}>
                    <button
                        onClick={() => handlePageChange(page)}
                        className={`page-numbers ${currentPage === page ? 'current' : ''}`}
                        aria-label={`Go to page ${page}`}
                    >
                        {page}
                    </button>
                </li>
            )
        );
    };

    if (error) {
        return (
            <div className="accent-custom">
                <Header />
                <main>
                    <div className="container py-5">
                        <div className="alert alert-danger text-center">
                            <h4>Error</h4>
                            <p>{error}</p>
                            <button
                                className="btn btn-primary"
                                onClick={handleRetry}
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="accent-custom">
            <Header />
            <main>
                <section className="tp-shop-area pt-50 pb-50" id="brands">
                    <div className="container">
                        <div className="row">
                            <div className="col-xl-12">
                                <div className="tp-shop-main-wrapper">
                                    <div className="tp-shop-top mb-45">
                                        <div className="row">
                                            <div className="col-xl-8">
                                                <h3 className="tp-section-title">Brands</h3>
                                            </div>

                                            <div className="col-xl-4">
                                                <div className="tp-header-search-2" style={{ position: 'relative' }}>
                                                    <form
                                                        onSubmit={(e) => {
                                                            e.preventDefault();
                                                            setConfirmedSearch(searchTerm.trim());
                                                            setCurrentPage(1);
                                                            setShowSuggestions(false);
                                                        }}
                                                    >
                                                        <input
                                                            type="text"
                                                            className="search_frmcntrl"
                                                            placeholder="Search for Brands..."
                                                            value={searchTerm}
                                                            onFocus={() => {
                                                                if (suggestions.length > 0) setShowSuggestions(true);
                                                            }}
                                                            onBlur={() => {
                                                                setTimeout(() => setShowSuggestions(false), 150); // delay to allow click
                                                            }}
                                                            // onChange={(e) => {
                                                            //     const value = e.target.value;
                                                            //     setSearchTerm(value);

                                                            //     if (value.trim() === '') {
                                                            //         setConfirmedSearch('');
                                                            //         setCurrentPage(1);
                                                            //         setSuggestions([]);
                                                            //         setShowSuggestions(false);
                                                            //     } else {
                                                            //         fetchSuggestions(value);
                                                            //     }
                                                            // }}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                setSearchTerm(value);

                                                                const trimmed = value.trim();

                                                                if (trimmed === '') {
                                                                    setConfirmedSearch('');
                                                                    setCurrentPage(1);
                                                                    setSuggestions([]);
                                                                    setShowSuggestions(false);
                                                                } else if (trimmed.length > 2) {
                                                                    fetchSuggestions(trimmed);
                                                                } else {
                                                                    setSuggestions([]);
                                                                    setShowSuggestions(false);
                                                                }
                                                            }}

                                                        />
                                                        <button type="submit">
                                                            <i className="fa-solid fa-magnifying-glass" />
                                                        </button>
                                                    </form>
                                                    <div
                                                        className="search_suggestion scrollbar_style"
                                                        style={{
                                                            display: showSuggestions && suggestions.length > 0 ? 'block' : 'none',
                                                            position: 'absolute',
                                                            top: '100%',
                                                            left: 0,
                                                            right: 0,
                                                            background: '#fff',
                                                            border: '1px solid #ccc',
                                                            zIndex: 1000,
                                                            maxHeight: '200px',
                                                            overflowY: 'auto'
                                                        }}
                                                    >
                                                        <ul>
                                                            {suggestions.map((item, index) => (
                                                                <li key={index}>
                                                                    <div
                                                                        onMouseDown={(e) => {
                                                                            e.preventDefault();
                                                                            setSearchTerm(item);
                                                                            setConfirmedSearch(item);
                                                                            setShowSuggestions(false);
                                                                            setCurrentPage(1);
                                                                        }}
                                                                    >
                                                                        {item}
                                                                    </div>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>

                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="tp-shop-items-wrapper tp-shop-item-primary">
                                        <div className="row">
                                            {loading ? (
                                                <div className="row">
                                                    {Array(12).fill(0).map((_, i) => (
                                                        <div className="col-xl-3 col-md-6 col-sm-6" key={i}>
                                                            <div className="tp-product-item mb-40">
                                                                <div className="tp-product-thumb">
                                                                    <Skeleton height={250} />
                                                                </div>
                                                                <div className="tp-product-content">
                                                                    <Skeleton height={20} width={150} style={{ marginBottom: 6 }} />
                                                                    <Skeleton height={20} width={100} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                brands.map((brand) => (
                                                    <div className="col-xl-3 col-lg-4 col-md-6 col-sm-6" key={brand._id}>
                                                        <div className="tp-product-item mb-40">
                                                            <div className="tp-product-thumb p-relative z-index-1 tp_advertisers">
                                                                <Link to={`/product/${brand._id}`} state={{ filterLabel: 'Brands' }}>
                                                                    <img
                                                                        src={brand.brandLogo || "/assets/img/brands/no_brand.png"}
                                                                        alt={`${brand.name} logo`}
                                                                        loading="lazy"
                                                                    />
                                                                </Link>
                                                            </div>
                                                            <div className="tp-product-content">
                                                                <h3 className="tp-product-title pt-10">
                                                                    <Link to={`/product/${brand._id}`} state={{ filterLabel: 'Brands' }}>{brand.name}</Link>
                                                                </h3>
                                                                <div className="tp-product-list-add-to-cart pt-15">
                                                                    <Link
                                                                        to={`/product/${brand._id}`}
                                                                        state={{ filterLabel: 'Brands' }}
                                                                        className="tp-product-list-add-to-cart-btn"
                                                                    >
                                                                        View Products
                                                                    </Link>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>

                                    {totalPages > 1 && (
                                        <div className="tp-shop-pagination mt-20 d-inline-flex w-100 justify-content-center">
                                            <div className="tp-pagination">
                                                <nav aria-label="Brand pagination">
                                                    <ul>
                                                        <li>
                                                            <button
                                                                className={`tp-pagination-prev prev page-numbers ${currentPage === 1 ? 'disabled' : ''}`}
                                                                onClick={() => handlePageChange(currentPage - 1)}
                                                                aria-label="Go to previous page"
                                                            >
                                                                <i className="fa-solid fa-arrow-left" />
                                                            </button>
                                                        </li>
                                                        {renderPaginationItems()}
                                                        <li>
                                                            <button
                                                                className={`next page-numbers ${currentPage === totalPages ? 'disabled' : ''}`}
                                                                onClick={() => handlePageChange(currentPage + 1)}
                                                                aria-label="Go to next page"
                                                            >
                                                                <i className="fa-solid fa-arrow-right" />
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </nav>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default Home;